import React, { useEffect, useState } from 'react'
import { useApiCall } from '../../../component/custom-hooks/api-call';
import { card, EditableCell, EmojiField, Section } from '.';
import { set } from 'date-fns';
import DataNotFound from '../../../component/ui/loader/data-not-found';
import InnerLoader from '../../../component/ui/loader/inner-loader';
import { MapPin } from 'lucide-react';


const Location = ({ toast = () => { }, inpStyle, C }) => {
    const [isLoading, setIsLoading] = useState(true);
    const apiCall = useApiCall();
    const selStyle = { ...inpStyle, cursor: "pointer" };

    // Location actions
    const [locationForm, setLocationForm] = useState({});
    const [modalLocation, setModalLocation] = useState(null);
    const openNewLocation = () => {
        setLocationForm({ name: "", note: "", emoji: "🏢", color: "#6b7280", is_active: true });
        setModalLocation(true);
    };
    const [locations, setLocations] = useState([]);
    const [colorTimeouts, setColorTimeouts] = useState({});
    // const updateLoc = (id, field, val) => setLocations(p => p.map(l => l.id === id ? { ...l, [field]: val } : l));
    const fetchLocations = async () => {
        setIsLoading(true);
        try {
            const res = await apiCall({ url: "cash-flow/locations/list", method: "GET" });
            setLocations(res?.data || []);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching locations:", error);
        }
    };
    const updateLoc = async (id, field, val) => {
        // Extract value if it's an event object
        const value = val?.target?.value !== undefined ? val.target.value : val;

        const newdata = locations.map(c => c.id === id ? { ...c, [field]: value } : c);
        setLocations(newdata);
        const updatedLocation = newdata.find(c => c.id === id);

        // Prepare data to send to API
        const dataToSend = {
            name: updatedLocation?.name,
            color: updatedLocation?.color,
            emoji: updatedLocation?.emoji,
            note: updatedLocation?.note,
            is_active: updatedLocation?.is_active,
            id: updatedLocation?.id
        };
        // Only API call, local update done separately
        try {
            await apiCall({
                url: `cash-flow/locations/update`,
                method: "POST",
                data: dataToSend
            });
            toast("Location updated", "success");
            // Don't refetch, since local is already updated
        } catch (error) {
            console.error("Error updating location:", error);
            // Revert local change on error
            fetchLocations();
        }
    };
    const addLocation = async () => {
        try {
            await apiCall({
                url: "cash-flow/locations/add",
                method: "POST",
                data: locationForm
            });
            toast("Location added", "success");
            setModalLocation(false);
            setLocationForm({});
            fetchLocations();
        } catch (error) {
            toast(error?.response?.data?.message || "Error adding location", "error");
        }
    };
    const toggleLocActive = async (id) => {
        // updateLoc(id, "is_active", !locations.find(l => l.id === id).is_active);
        const newdata = locations.map(c => c.id === id ? { ...c, is_active: !c.is_active } : c);
        setLocations(newdata);
        try {
            await apiCall({
                url: `cash-flow/locations/${id}/update-status`,
                method: "POST",
            });
            toast("Location updated", "success");
            // fetchLocations();
        } catch (error) {
            console.error("Error toggling location active status:", error?.message || error);
            toast("Error updating location", "error");
            fetchLocations();
        }
    };

    // ALL API CALLS ON LOAD
    useEffect(() => {
        fetchLocations();
    }, []);

    return (
        <Section
            id="locations"
            icon={<MapPin size={18} />}
            title="Locations"
            desc="Offices and sites — used to categorise every expense"
            C={C}
            card={card}
            Btn={
                <button onClick={() => { openNewLocation() }}
                    style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 9, border: `1px dashed ${C.border}`, background: "#f9fafb", fontSize: 12, fontWeight: 600, color: C.textSec, cursor: "pointer" }}>
                    + Add Office / Location
                </button>
            }
        >
            {isLoading ? (
                <div className="w-full h-[420px] flex items-center justify-center">
                    <InnerLoader />
                </div>
            ) : locations.length < 1 || !locations.length ? (
                <div className="w-full h-[420px] flex items-center justify-cente pb-5">
                    <DataNotFound title={"locations"} />
                </div>
            ) : (
                <div className='grid grid-cols-2 gap-[12px]' style={{ marginBottom: 14 }}>
                    {locations.map(loc => (
                        <div key={loc.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 11, border: `1px solid ${loc.is_active ? C.border : "#e5e7eb"}`, background: loc.is_active ? "#fff" : "#f9fafb", opacity: loc.is_active ? 1 : 0.6, transition: "all 0.15s" }}>
                            <div style={{ width: 10, height: 10, borderRadius: 3, background: loc.color, flexShrink: 0 }} />
                            <span style={{ fontSize: 18, flexShrink: 0 }}>
                                <EditableCell type='emoji' value={loc.emoji || "X"} onSave={v => updateLoc(loc.id, "emoji", v)} placeholder="X" style={{ fontSize: 16, width: 40, textAlign: "center" }} />
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <EditableCell value={loc.name} placeholder='No Name' onSave={v => { updateLoc(loc.id, "name", v); }} style={{ fontSize: 13, fontWeight: 700, color: C.textPri }} />
                            </div>
                            <EditableCell value={loc.note} onSave={v => updateLoc(loc.id, "note", v)} placeholder="Add note…" style={{ fontSize: 11, color: C.textMut, minWidth: 120 }} />
                            {/* Color picker */}
                            <input type="color" value={loc.color} onChange={e => {
                                // Update local state immediately
                                setLocations(p => p.map(l => l.id === loc.id ? { ...l, color: e.target.value } : l));
                                // Clear existing timeout
                                if (colorTimeouts[loc.id]) clearTimeout(colorTimeouts[loc.id]);
                                // Set new timeout for API call
                                const timeout = setTimeout(() => {
                                    updateLoc(loc.id, "color", e.target.value);
                                    setColorTimeouts(p => ({ ...p, [loc.id]: null }));
                                }, 500);
                                setColorTimeouts(p => ({ ...p, [loc.id]: timeout }));
                            }}
                                style={{ width: 28, height: 28, borderRadius: 7, border: `1px solid ${C.border}`, cursor: "pointer", padding: 2, background: "#fff" }} />
                            {/* <button onClick={() => toggleLocActive(loc.id)}
                                style={{ padding: "4px 12px", borderRadius: 20, border: `1px solid ${loc.is_active ? "#fecaca" : C.border}`, background: loc.is_active ? "#fef2f2" : "#f0fdf4", fontSize: 11, fontWeight: 600, color: loc.is_active ? C.red : C.green, cursor: "pointer", whiteSpace: "nowrap" }}>
                                {loc.is_active ? "Deactivate" : "Activate"}
                            </button> */}

                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={loc.is_active}
                                    onChange={() => { toggleLocActive(loc.id); }}
                                />

                                {/* Background */}
                                <div className="w-12 h-7 bg-[rgba(0,0,0,0.55)] rounded-full peer peer-checked:bg-black transition-colors"></div>

                                {/* Circle */}
                                <div className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                            </label>
                        </div>
                    ))}
                </div>
            )}


            {/* Location modal */}
            {modalLocation && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
                    <div style={{ background: "#fff", borderRadius: 16, padding: "24px", width: "100%", maxWidth: 460 }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: "0 0 18px", fontSize: 16, fontWeight: 800, color: C.textPri }}>
                            Add Loaction
                        </h3>

                        <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>
                                Location Name
                            </div>
                            <input type={'text'} value={locationForm.name} onChange={e => setLocationForm({ ...locationForm, name: e.target.value })} placeholder={"Location name"} style={{ ...inpStyle, width: "100%" }} />
                        </div>

                        <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>
                                Note
                            </div>
                            <input type={'text'} value={locationForm.note} onChange={e => setLocationForm({ ...locationForm, note: e.target.value })} placeholder={"Note"} style={{ ...inpStyle, width: "100%" }} />
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                            <div style={{ position: "relative" }}>
                                <EmojiField
                                    value={locationForm.emoji}
                                    onChange={e => setLocationForm({ ...locationForm, emoji: `${e}` })}
                                    C={C}
                                    style={{ ...inpStyle, width: "100%", paddingRight: 30, paddingTop: 4, paddingBottom: 4, fontSize: 12 }}
                                />
                            </div>

                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>
                                    Color
                                </div>
                                <input type="color" value={locationForm.color} onChange={e => setLocationForm({ ...locationForm, color: e.target.value })} style={{ ...selStyle, width: "100%" }} />
                            </div>

                            <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5 }}>Status</div>
                                <select value={locationForm.is_active} onChange={e => setLocationForm({ ...locationForm, is_active: e.target.value === "true" })} style={{ ...selStyle, width: "100%" }}>
                                    <option value={true}>Active</option>
                                    <option value={false}>Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button
                                onClick={() => setModalLocation(null)}
                                style={{ flex: 1, padding: "9px", borderRadius: 9, border: `1px solid ${C.border}`, background: "#fff", fontSize: 13, cursor: "pointer", color: C.textSec }}
                            >
                                Cancel
                            </button>

                            <button onClick={addLocation}
                                style={{ flex: 1, padding: "9px", borderRadius: 9, border: "none", background: C.accent, fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer" }}>
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Section>
    )
}

export default Location
